import React, { FC, useMemo } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import Toast from './components/core/Toast';
import { IntlProvider } from 'react-intl';
import { locales, SupportedLocale } from './locales/locale-config';
import * as Sentry from '@sentry/react';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

// needed to make toast work
import 'react-toastify/dist/ReactToastify.min.css';
import { ConfigProvider } from './contexts/ConfigContext';
import { useAnimations } from './stores/animations';
import { getSentryEnvironment } from './helpers/sentry';
import { reportApiError } from './utils/reportApiError';
import { AppUpdateNotice } from './components/AppUpdateNotice';

Sentry.init({
  enabled: process.env.NODE_ENV === 'production',
  environment: getSentryEnvironment(window.location.hostname),
  dsn: 'https://9f83cb883b63c4f198241edc78b1033f@o1409683.ingest.us.sentry.io/4509389510410240',
  tunnel: 'https://sentry.allbin.se',
  sendDefaultPii: true,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 0.05,
  replaysOnErrorSampleRate: 0.05,
  replaysSessionSampleRate: 0,
});

const App: FC = Sentry.withProfiler(() => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({ onError: reportApiError }),
      }),
    [],
  );

  const defaultLocale: SupportedLocale = 'sv';

  const currentLocale = useMemo<SupportedLocale>(
    () =>
      (localStorage.getItem('preferredLocale') as SupportedLocale) ||
      defaultLocale,
    [],
  );

  React.useEffect(() => {
    localStorage.setItem('preferredLocale', currentLocale);
    document.documentElement.lang = currentLocale;
    document.documentElement.setAttribute('xml:lang', currentLocale);
    document.body.lang = currentLocale;
    document.body.setAttribute('xml:lang', currentLocale);
  }, [currentLocale]);

  const animations = useAnimations((props) => props.enabled);

  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider
        locale={currentLocale}
        defaultLocale={defaultLocale}
        messages={locales[currentLocale]}
      >
        <ConfigProvider>
          <div className="relative min-h-full w-full font-sans font-light">
            <div className="relative h-screen w-full overflow-x-hidden bg-background-50 text-text-900">
              <RouterProvider router={router} />
            </div>
            <Toast animations={animations} />
            <AppUpdateNotice />
          </div>
        </ConfigProvider>
      </IntlProvider>
    </QueryClientProvider>
  );
});

export default App;
